import React, { useEffect, useState, useRef } from 'react';
import { Upload, Globe, ChevronDown, X } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Language } from '../i18n/translations';
const languageLabels: Record<Language, string> = {
  en: 'EN',
  ua: 'UA',
  ru: 'RU',
  ka: 'KA'
};
const languageNames: Record<Language, string> = {
  en: 'English',
  ua: 'Українська',
  ru: 'Русский',
  ka: 'ქართული'
};
export function Header() {
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target as Node)) {
        setLanguageDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const getLink = (path: string) => `/${language}${path}`;
  return <>
      <header className="w-full bg-white py-4 px-6 md:px-12 sticky top-0 z-50 shadow-sm" data-id="element-183">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4" data-id="element-184">
          {/* Logo — clip saarone subtitle from bottom */}
          <a href={getLink('/')} className="flex items-center flex-shrink-0" data-id="element-185">
            <img src="https://cdn.magicpatterns.com/uploads/4nkahh2hNsVZTnNq46Gbps/elvora_logo_cutout.png" alt="ELVORA" className="h-12 w-auto" width={120} height={48} fetchPriority="high" style={{
            clipPath: 'inset(0 0 18% 0)'
          }} data-id="element-186" />
          </a>

          {/* Center Navigation - Desktop: Companies first */}
          <nav className="hidden lg:flex items-center justify-center gap-3 flex-1" data-id="element-187">
            <a href={getLink('/companies')} className="px-5 py-2 text-sm font-medium text-[var(--color-text-dark)] rounded-full border border-gray-200 hover:border-gray-300 transition-colors" data-id="element-188">
              {t('header.forCompanies')}
            </a>
            <a href={getLink('/specialists')} className="px-5 py-2 text-sm font-medium text-[var(--color-text-dark)] rounded-full border border-gray-200 hover:border-gray-300 transition-colors" data-id="element-189">
              {t('header.forSeafarers')}
            </a>
          </nav>

          {/* Right side - Language + Upload */}
          <div className="flex items-center gap-3" data-id="element-190">
            {/* Language Dropdown - Desktop */}
            <div className="hidden md:block relative" ref={dropdownRef} data-id="element-191">
              <button onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-full hover:border-gray-300 transition-colors" data-id="element-192">
                <Globe className="w-4 h-4 text-gray-500" data-id="element-193" />
                <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-194">
                  {languageLabels[language]}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-500" data-id="element-195" />
              </button>

              {languageDropdownOpen && <div className="absolute top-full right-0 mt-2 w-36 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-196">
                  {(Object.keys(languageNames) as Language[]).map(lang => <button key={lang} onClick={() => {
                setLanguage(lang);
                setLanguageDropdownOpen(false);
              }} className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors ${language === lang ? 'bg-[var(--color-tan)]/10 text-[var(--color-tan)] font-medium' : 'text-[var(--color-text-dark)]'}`} data-id="element-197">
                      {languageNames[lang]}
                    </button>)}
                </div>}
            </div>

            {/* Upload Resume Button - Desktop */}
            <a href={getLink('/specialists')} className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-[var(--color-tan)] text-white text-sm font-medium rounded-full hover:opacity-90 transition-opacity" data-id="element-198">
              <Upload className="w-4 h-4" data-id="element-199" />
              <span className="hidden lg:inline" data-id="element-200">{t('header.sendCV')}</span>
            </a>

            {/* Mobile - Language + Send Resume */}
            <div className="flex md:hidden items-center gap-2" ref={mobileDropdownRef} data-id="element-201">
              <div className="relative" data-id="element-202">
                <button onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)} className="p-2.5 border border-gray-200 rounded-full hover:border-gray-300 transition-colors" data-id="element-203">
                  <Globe className="w-5 h-5 text-gray-500" data-id="element-204" />
                </button>

                {languageDropdownOpen && <div className="absolute top-full right-0 mt-2 w-36 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-50" data-id="element-205">
                    {(Object.keys(languageNames) as Language[]).map(lang => <button key={lang} onClick={() => {
                  setLanguage(lang);
                  setLanguageDropdownOpen(false);
                }} className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors ${language === lang ? 'bg-[var(--color-tan)]/10 text-[var(--color-tan)] font-medium' : 'text-[var(--color-text-dark)]'}`} data-id="element-206">
                        {languageNames[lang]}
                      </button>)}
                  </div>}
              </div>
              <a href={getLink('/specialists')} className="flex items-center gap-2 px-4 py-2.5 bg-[var(--color-tan)] text-white text-sm font-medium rounded-full hover:opacity-90 transition-opacity" data-id="element-207">
                <Upload className="w-4 h-4" data-id="element-208" />
                <span data-id="element-209">{t('header.sendCV')}</span>
              </a>
            </div>

            {/* Mobile menu button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2" aria-label="Menu" data-id="element-210">
              {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-211" /> : <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-212">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" data-id="element-213" />
                </svg>}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileMenuOpen(false)} data-id="element-214">
          <div className="absolute right-0 top-0 h-full w-64 bg-white shadow-xl p-6" onClick={e => e.stopPropagation()} data-id="element-215">
            <div className="flex justify-between items-center mb-8" data-id="element-216">
              <h3 className="text-lg font-semibold text-[var(--color-text-dark)]" data-id="element-217">
                {t('header.menu')}
              </h3>
              <button onClick={() => setMobileMenuOpen(false)} data-id="element-218">
                <X className="w-6 h-6" data-id="element-219" />
              </button>
            </div>

            <nav className="space-y-4" data-id="element-220">
              <a href={getLink('/companies')} className="block px-4 py-2 text-sm font-medium text-[var(--color-text-dark)] hover:bg-gray-50 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-id="element-221">
                {t('header.forCompanies')}
              </a>
              <a href={getLink('/specialists')} className="block px-4 py-2 text-sm font-medium text-[var(--color-text-dark)] hover:bg-gray-50 rounded-lg" onClick={() => setMobileMenuOpen(false)} data-id="element-222">
                {t('header.forSeafarers')}
              </a>
            </nav>
          </div>
        </div>}
    </>;
}